import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { activityQueryKey } from '@/hooks/use-activity'

export type MeetingEntityType = 'party' | 'opportunity' | 'project' | 'club' | 'introduction'
export type MeetingSource = 'granola' | 'plaud'

/**
 * 'held' is Plaud-only: the recording is in FRIDAY but deliberately not in the
 * Company Brain, because it neither matched a calendar meeting nor carried the
 * marker. It has to be promoted before it can be linked.
 */
export type MeetingStatus = 'held' | 'unlinked' | 'linked' | 'dismissed'

export interface Meeting {
  id: string
  org_id: string
  source: MeetingSource
  external_id: string
  title: string
  meeting_date: string
  transcript: string
  summary: string | null
  /** JSON array of strings, or null. Plaud only. */
  action_items: string | null
  /** JSON array of diarised speaker labels, or null. Plaud only. */
  speakers: string | null
  duration_s: number | null
  device_id: string | null
  status: MeetingStatus
  promote_reason: 'calendar' | 'marker' | 'manual' | null
  matched_event_id: string | null
  event_tag: string | null
  linked_entity_type: MeetingEntityType | null
  linked_entity_id: string | null
  linked_activity_id: string | null
  links?: string
  created_at: number
}

const MEETINGS_KEY = ['meetings'] as const

/** Parse a JSON-array column without letting bad data break the row. */
export function parseList(raw: string | null): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

export function useMeetings(status: MeetingStatus = 'unlinked', source?: MeetingSource) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, status, source ?? 'all'],
    queryFn: () => {
      const qs = new URLSearchParams({ status })
      if (source) qs.set('source', source)
      return api.get<Meeting[]>(`/integrations/meetings?${qs}`)
    },
  })
}

export function useSyncGranola() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ synced: number; unlinked: number }>('/integrations/meetings/sync/granola', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useSyncPlaud() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ synced: number; promoted: number; held: number; autoLinked: number; unlinked: number; pending: number; backfilled: number; classified: number; suggested: number; classificationErrors: number; classificationSkipped?: string }>(
        '/integrations/meetings/sync/plaud',
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useBackfillPlaud() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (since: string) => api.post<{ synced: number; promoted: number; held: number; autoLinked: number; unlinked: number; pending: number; backfilled: number; classified: number; suggested: number; classificationErrors: number; classificationSkipped?: string }>(
      `/integrations/meetings/sync/plaud?since=${encodeURIComponent(since)}`, {},
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useLinkMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; entity_type: MeetingEntityType; entity_id: string }) =>
      api.post(`/integrations/meetings/${vars.id}/link`, {
        entity_type: vars.entity_type,
        entity_id: vars.entity_id,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY })
      qc.invalidateQueries({ queryKey: activityQueryKey(vars.entity_type, vars.entity_id) })
    },
  })
}

export function useMeetingEventTags() {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'event-tags'],
    queryFn: () => api.get<string[]>('/integrations/meetings/event-tags'),
  })
}

export interface MeetingEventGroup {
  name: string
  recordings: number
  held: number
  unlinked: number
  linked: number
  first_recording: string
  last_recording: string
}

export function useMeetingEventGroups() {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'event-groups'],
    queryFn: () => api.get<MeetingEventGroup[]>('/integrations/meetings/event-groups'),
  })
}

export function useClassifyGranola() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ classified: number; linked: number; suggested: number; skipped?: string }>(
      '/integrations/meetings/classify/granola', {},
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useMeetingEventSuggestions(id: string) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, id, 'event-suggestions'],
    queryFn: () => api.get<string[]>(`/integrations/meetings/${id}/event-suggestions`),
  })
}

export function useSetMeetingEventTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, event_tag }: { id: string; event_tag: string }) =>
      api.patch(`/integrations/meetings/${id}/event-tag`, { event_tag }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useClearMeetingLinks() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/meetings/${id}/links`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY })
      qc.invalidateQueries({ queryKey: ['activity'] })
    },
  })
}

/** Move a held recording into the Brain and the inbox. */
export function usePromoteMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ promoted: boolean; classification?: { linked: number; suggested: number; skipped?: string } }>(`/integrations/meetings/${id}/promote`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}

export function useDismissMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/integrations/meetings/${id}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  })
}
